import { Controller } from "@hotwired/stimulus"

type MediaItem = {
  type: "image" | "video"
  url: string
  caption?: string | null
}

const IMAGE_DURATION = 3000

export default class extends Controller {
  static values = {
    media: Array,
    closeLabel: String,
  }

  declare mediaValue: MediaItem[]
  declare closeLabelValue: string

  overlay: HTMLDivElement | null = null
  stage: HTMLDivElement | null = null
  index = 0
  timer: number | null = null
  boundKeydown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      this.stop()
    }
  }
  boundFullscreenChange = () => {
    if (!document.fullscreenElement && this.overlay) {
      this.stop()
    }
  }

  disconnect() {
    this.stop()
  }

  start() {
    if (this.overlay || this.mediaValue.length === 0) {
      return
    }

    this.buildOverlay()
    document.addEventListener("keydown", this.boundKeydown)
    document.addEventListener("fullscreenchange", this.boundFullscreenChange)

    this.overlay!.requestFullscreen?.().catch(() => {
      // Fullscreen can be denied; the overlay still covers the viewport.
    })

    this.index = 0
    this.show()
  }

  stop() {
    this.clearTimer()
    document.removeEventListener("keydown", this.boundKeydown)
    document.removeEventListener("fullscreenchange", this.boundFullscreenChange)

    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {})
    }

    this.overlay?.remove()
    this.overlay = null
    this.stage = null
  }

  private buildOverlay() {
    const overlay = document.createElement("div")
    overlay.className = "presentation-mode-overlay"

    const closeButton = document.createElement("button")
    closeButton.type = "button"
    closeButton.className = "presentation-mode-close"
    closeButton.setAttribute("aria-label", this.closeLabelValue || "Close")
    closeButton.textContent = "×"
    closeButton.addEventListener("click", () => this.stop())

    const stage = document.createElement("div")
    stage.className = "presentation-mode-stage"

    overlay.append(closeButton, stage)
    document.body.appendChild(overlay)

    this.overlay = overlay
    this.stage = stage
  }

  private show() {
    if (!this.stage) {
      return
    }

    const item = this.mediaValue[this.index % this.mediaValue.length]
    this.stage.replaceChildren()

    if (item.type === "video") {
      const video = document.createElement("video")
      video.src = item.url
      video.autoplay = true
      video.muted = false
      video.playsInline = true
      video.className = "presentation-mode-media"
      video.addEventListener("ended", () => this.next())
      video.addEventListener("error", () => this.scheduleNext(IMAGE_DURATION))
      this.stage.appendChild(video)
      video.play().catch(() => {
        // Autoplay with sound can be blocked; retry muted.
        video.muted = true
        video.play().catch(() => this.scheduleNext(IMAGE_DURATION))
      })
    } else {
      const image = document.createElement("img")
      image.src = item.url
      image.alt = item.caption || ""
      image.className = "presentation-mode-media"
      this.stage.appendChild(image)
      this.scheduleNext(IMAGE_DURATION)
    }
  }

  private next() {
    this.clearTimer()
    this.index = (this.index + 1) % this.mediaValue.length
    this.show()
  }

  private scheduleNext(delay: number) {
    this.clearTimer()
    this.timer = window.setTimeout(() => this.next(), delay)
  }

  private clearTimer() {
    if (this.timer !== null) {
      window.clearTimeout(this.timer)
      this.timer = null
    }
  }
}
